import React, {useEffect, useMemo, useState} from "react";
import {useCsContext} from "../../state/CsInstance";
import styles from "./FileTree.module.scss";
import {ErrorBoundary} from "react-error-boundary";
import ErrorComponent from "../ErrorComponent/ErrorComponent";
import ModalConfirmContent from "../Modals/ModalConfirmContent";
import {observer} from "mobx-react-lite";
import FileLeaf from "./FileLeaf";

const RESET_TITLE = "Are you sure you want to reset this file?";
const DELETE_TITLE = "Are you sure you want to delete this file?";
const RESET_TEXT = "This will undo all changes you made to this file.";
const DELETE_TEXT =
	"Because this file does not exist in the original set of files, it will be deleted from the project.";

const FileTree: React.FC = () => {
	const {codeStore, globalModalStore, theaterStore} = useCsContext();
	const {visibleFileNames, visibleFileMap: visibleFileData} = codeStore;
	const [resetFile, setResetFile] = useState("");
	const isOriginal = visibleFileData?.[resetFile]?.attributes?.isOriginal;

	const ConfirmModalContent = useMemo<React.JSX.Element>(() => {
		return (
			<ModalConfirmContent
				title={isOriginal ? RESET_TITLE : DELETE_TITLE}
				text={isOriginal ? RESET_TEXT : DELETE_TEXT}
				onCancel={() => {
					setResetFile("");
				}}
				onSuccess={() => {
					theaterStore.toggleShowManifest(false);
					codeStore.resetFile(resetFile);
					setResetFile("");
				}}
			/>
		);
	}, [resetFile, codeStore, theaterStore, isOriginal]);

	// Trigger reset modal to open whenever resetFile is set
	useEffect(() => {
		if (resetFile) {
			globalModalStore.showModal(ConfirmModalContent);
		}
	}, [resetFile, globalModalStore, ConfirmModalContent]);

	// Sort files by superclass
	const superclassDictionary: {[superclass: string]: string[]} = {};
	visibleFileNames.forEach((filename) => {
		const data = visibleFileData[filename];
		if (!data) {
			return <></>; // Shouldn't happen, but just in case
		}
		// Check if it's a Java file with "extends" keyword to determine superclass
		const superclassMatch =
			filename.match(/.java$/) && data.value.match(/extends (\w+)/);

		// Map the file to the correct superclass. If superclass is not found, default to Utilities.
		const superclass =
			superclassMatch &&
			(visibleFileNames.includes(superclassMatch[1] + ".java") || // Check if superclass file exists
				superclassMatch[1] === "Actor" || // Check if superclass is Actor (file doesn't appear in fileNames)
				superclassMatch[1] === "Stage") // Check if superclass is Stage
				? (superclassMatch[1] as string)
				: "Utilities";

		if (!superclassDictionary[superclass]) {
			superclassDictionary[superclass] = [];
		}

		superclassDictionary[superclass]?.push(filename);
		superclassDictionary[superclass]?.sort();
	});

	return (
		<ErrorBoundary FallbackComponent={ErrorComponent}>
			<div className={styles.fileTreeContainer} id="fileTree">
				<div className={styles.fileHeaderContainer}>
					<div className={styles.fileHeader}>Files</div>
				</div>
				<FileLeaf
					superclassDictionary={superclassDictionary}
					nodeName="Stage"
					setResetFileAndOpenModal={setResetFile}
				/>
				<FileLeaf
					superclassDictionary={superclassDictionary}
					nodeName="Actor"
					setResetFileAndOpenModal={setResetFile}
				/>
				<FileLeaf
					superclassDictionary={superclassDictionary}
					nodeName="Utilities"
					setResetFileAndOpenModal={setResetFile}
				/>
			</div>
		</ErrorBoundary>
	);
};

export default observer(FileTree);
